/**
 * LoadingDesigns — 로딩 디자인 관리 페이지
 *
 * 로딩 디자인 목록 + ZIP 업로드로 새 로딩 디자인 생성
 */

import { useState, useCallback, useRef } from 'react'
import { useApiFetch } from '../hooks/useApiFetch'
import { useToast } from '../components/ToastContainer'
import { apiFetch } from '../lib/api'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { Spinner } from '../ui/Spinner'
import { Modal } from '../ui/Modal'
import { ConfirmDialog } from '../ui/ConfirmDialog'
import styles from './Templates.module.css'

// ── 타입 ──

interface LoadingDesign {
  id: string
  name: string
  slug: string
  description: string | null
  thumbnailUrl: string | null
  cachedHtml: string | null
  isDefault: boolean
  isActive: boolean
  sortOrder: number
  createdAt: string
}

interface LoadingDesignsResponse {
  loadingDesigns: LoadingDesign[]
}

// ── 유틸 ──

function fileNameToName(fileName: string): string {
  return fileName.replace(/\.(zip|html|htm)$/i, '').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim()
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function toSlug(name: string): string {
  return name.toLowerCase()
    .replace(/[^a-z0-9가-힣\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 64)
}

interface ZipAnalysis {
  totalFiles: number
  totalSize: number
  htmlFiles: string[]
  cssFiles: string[]
  fontFiles: string[]
  imageFiles: string[]
  otherFiles: string[]
}

async function analyzeFile(file: File): Promise<ZipAnalysis | null> {
  if (!file.name.endsWith('.zip')) return null

  const buffer = await file.arrayBuffer()
  const view = new DataView(buffer)
  const entries: { name: string; size: number }[] = []

  let eocdOffset = -1
  for (let i = buffer.byteLength - 22; i >= 0; i--) {
    if (view.getUint32(i, true) === 0x06054b50) { eocdOffset = i; break }
  }
  if (eocdOffset === -1) return null

  const cdOffset = view.getUint32(eocdOffset + 16, true)
  const cdEntries = view.getUint16(eocdOffset + 10, true)

  let pos = cdOffset
  for (let i = 0; i < cdEntries && pos < buffer.byteLength; i++) {
    if (view.getUint32(pos, true) !== 0x02014b50) break
    const nameLen = view.getUint16(pos + 28, true)
    const extraLen = view.getUint16(pos + 30, true)
    const commentLen = view.getUint16(pos + 32, true)
    const uncompSize = view.getUint32(pos + 24, true)
    const nameBytes = new Uint8Array(buffer, pos + 46, nameLen)
    const name = new TextDecoder().decode(nameBytes)
    if (!name.endsWith('/')) entries.push({ name, size: uncompSize })
    pos += 46 + nameLen + extraLen + commentLen
  }

  const ext = (n: string) => n.split('.').pop()?.toLowerCase() ?? ''
  const filtered = entries.filter((e) => !e.name.startsWith('__MACOSX') && !e.name.startsWith('.'))

  return {
    totalFiles: filtered.length,
    totalSize: filtered.reduce((s, e) => s + e.size, 0),
    htmlFiles: filtered.filter((e) => ['html', 'htm'].includes(ext(e.name))).map((e) => e.name),
    cssFiles: filtered.filter((e) => ext(e.name) === 'css').map((e) => e.name),
    fontFiles: filtered.filter((e) => ['woff2', 'woff', 'ttf', 'otf'].includes(ext(e.name))).map((e) => e.name),
    imageFiles: filtered.filter((e) => ['png', 'jpg', 'jpeg', 'webp', 'svg', 'gif'].includes(ext(e.name))).map((e) => e.name),
    otherFiles: filtered.filter((e) => !['html', 'htm', 'css', 'woff2', 'woff', 'ttf', 'otf', 'png', 'jpg', 'jpeg', 'webp', 'svg', 'gif', 'js'].includes(ext(e.name))).map((e) => e.name),
  }
}

interface DropPreview {
  file: File
  name: string
  slug: string
  analysis: ZipAnalysis | null
}

// ── 컴포넌트 ──

export function LoadingDesigns() {
  const { data, isLoading, refetch } = useApiFetch<LoadingDesignsResponse>('/api/admin/loading-designs')
  const designs = data?.loadingDesigns ?? []
  const { addToast } = useToast()

  const [deleteTarget, setDeleteTarget] = useState<LoadingDesign | null>(null)
  const [deleting, setDeleting] = useState(false)

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await apiFetch(`/api/admin/loading-designs/${deleteTarget.id}`, { method: 'DELETE' })
      addToast({ type: 'success', message: `"${deleteTarget.name}" 삭제 완료` })
      setDeleteTarget(null)
      refetch()
    } catch (err: any) {
      addToast({ type: 'error', message: err.message || '삭제 실패' })
    } finally {
      setDeleting(false)
    }
  }, [deleteTarget, addToast, refetch])

  const handleSetDefault = useCallback(async (design: LoadingDesign) => {
    try {
      await apiFetch(`/api/admin/loading-designs/${design.id}/set-default`, { method: 'PUT' })
      addToast({ type: 'success', message: `"${design.name}" 기본 로딩으로 설정` })
      refetch()
    } catch (err: any) {
      addToast({ type: 'error', message: err.message || '기본값 설정 실패' })
    }
  }, [addToast, refetch])

  const handleToggleActive = useCallback(async (design: LoadingDesign) => {
    try {
      await apiFetch(`/api/admin/loading-designs/${design.id}`, {
        method: 'PUT',
        body: JSON.stringify({ isActive: !design.isActive }),
      })
      addToast({ type: 'success', message: `"${design.name}" ${design.isActive ? '비활성화' : '활성화'} 완료` })
      refetch()
    } catch (err: any) {
      addToast({ type: 'error', message: err.message || '상태 변경 실패' })
    }
  }, [addToast, refetch])

  // 드래그앤드롭
  const [isDragOver, setIsDragOver] = useState(false)
  const dragCounter = useRef(0)
  const [dropPreview, setDropPreview] = useState<DropPreview | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState('')

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    dragCounter.current++
    if (dragCounter.current === 1) setIsDragOver(true)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => { e.preventDefault() }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    dragCounter.current--
    if (dragCounter.current === 0) setIsDragOver(false)
  }, [])

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    dragCounter.current = 0
    setIsDragOver(false)
    if (uploading) return

    const file = e.dataTransfer.files[0]
    if (!file) return

    const ext = file.name.split('.').pop()?.toLowerCase()
    if (!ext || !['zip', 'html', 'htm'].includes(ext)) {
      addToast({ type: 'error', message: 'ZIP 또는 HTML 파일만 업로드할 수 있습니다' })
      return
    }

    const name = fileNameToName(file.name)
    if (!name) {
      addToast({ type: 'error', message: '파일 이름에서 이름을 생성할 수 없습니다' })
      return
    }

    const analysis = await analyzeFile(file)
    setDropPreview({ file, name, slug: toSlug(name), analysis })
  }, [uploading, addToast])

  const handleConfirmUpload = useCallback(async () => {
    if (!dropPreview) return
    const { file, name, slug } = dropPreview

    setUploading(true)
    setUploadProgress('Presigned URL 발급 중...')

    try {
      const presign = await apiFetch<{ key: string; uploadUrl: string }>('/api/admin/loading-designs/presign', {
        method: 'POST',
        body: JSON.stringify({ fileName: file.name, contentType: file.type || 'application/octet-stream' }),
      })

      setUploadProgress(`S3 업로드 중... (${formatSize(file.size)})`)
      const s3Res = await fetch(presign.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      })
      if (!s3Res.ok) throw new Error(`S3 업로드 실패 (${s3Res.status})`)

      setUploadProgress('서버 처리 중...')
      await apiFetch('/api/admin/loading-designs/process-s3', {
        method: 'POST',
        body: JSON.stringify({ s3Key: presign.key, name, slug, fileName: file.name }),
      })

      addToast({ type: 'success', message: `로딩 디자인 "${name}" 생성 완료` })
      setDropPreview(null)
      refetch()
    } catch (err: any) {
      addToast({ type: 'error', message: err.message || '업로드 실패' })
    } finally {
      setUploading(false)
      setUploadProgress('')
    }
  }, [dropPreview, addToast, refetch])

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h2 className={styles.title}>로딩 디자인</h2>
      </div>

      {isLoading ? (
        <div className={styles.loading}><Spinner /></div>
      ) : (
        <>
          <div
            className={`${styles.dropZone} ${isDragOver ? styles.dropZoneActive : ''}`}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className={styles.dropZoneIcon}>&#8682;</div>
            <p className={styles.dropZoneTitle}>ZIP 또는 HTML 파일을 여기에 드래그하세요</p>
            <p className={styles.dropZoneDesc}>파일 이름이 로딩 디자인 이름으로 사용됩니다</p>
          </div>
          {designs.length > 0 && (
            <div className={styles.grid}>
              {designs.map((d) => (
                <div key={d.id} className={styles.card}>
                  <div className={styles.cardPreview}>
                    {d.thumbnailUrl ? (
                      <img src={d.thumbnailUrl} alt={d.name} />
                    ) : d.cachedHtml ? (
                      <iframe
                        srcDoc={d.cachedHtml}
                        title={d.name}
                        className={styles.cardIframe}
                      />
                    ) : (
                      <div className={styles.cardPlaceholder}>{d.name}</div>
                    )}
                  </div>
                  <div className={styles.cardInfo}>
                    <div className={styles.cardNameRow}>
                      <span className={styles.cardName}>{d.name}</span>
                      <button
                        type="button"
                        className={styles.cardDeleteBtn}
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(d) }}
                        title="삭제"
                      >
                        ×
                      </button>
                    </div>
                    <div className={styles.cardMeta}>
                      {d.isDefault && <Badge color="blue">기본</Badge>}
                      <Badge color={d.isActive ? 'green' : 'yellow'}>
                        {d.isActive ? '활성' : '비활성'}
                      </Badge>
                      {!d.isDefault && (
                        <Button variant="outline" size="sm" onClick={() => handleSetDefault(d)}>
                          기본으로 설정
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => handleToggleActive(d)}>
                        {d.isActive ? '비활성화' : '활성화'}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* 드롭 파일 미리보기 모달 */}
      <Modal
        open={dropPreview !== null}
        onClose={() => { if (!uploading) setDropPreview(null) }}
        title={uploading ? '업로드 중...' : '로딩 디자인 업로드 확인'}
      >
        {dropPreview && !uploading && (
          <div className={styles.previewModal}>
            <div className={styles.previewInfo}>
              <div className={styles.previewRow}>
                <span className={styles.previewLabel}>파일</span>
                <span className={styles.previewValue}>{dropPreview.file.name} ({formatSize(dropPreview.file.size)})</span>
              </div>
              <div className={styles.previewRow}>
                <span className={styles.previewLabel}>이름</span>
                <span className={styles.previewValue}>{dropPreview.name}</span>
              </div>
              <div className={styles.previewRow}>
                <span className={styles.previewLabel}>Slug</span>
                <span className={styles.previewValue}>{dropPreview.slug}</span>
              </div>
            </div>
            {dropPreview.analysis && (
              <div className={styles.previewAnalysis}>
                <div className={styles.previewAnalysisHeader}>
                  ZIP 구성 — {dropPreview.analysis.totalFiles}개 파일 ({formatSize(dropPreview.analysis.totalSize)})
                </div>
                <div className={styles.previewAnalysisBody}>
                  {dropPreview.analysis.htmlFiles.length > 0 && (
                    <div className={styles.previewGroup}>
                      <Badge color="blue">HTML ({dropPreview.analysis.htmlFiles.length})</Badge>
                      {dropPreview.analysis.htmlFiles.map((f) => <span key={f} className={styles.previewFile}>{f}</span>)}
                    </div>
                  )}
                  {dropPreview.analysis.fontFiles.length > 0 && (
                    <div className={styles.previewGroup}>
                      <Badge color="yellow">폰트 ({dropPreview.analysis.fontFiles.length})</Badge>
                      {dropPreview.analysis.fontFiles.map((f) => <span key={f} className={styles.previewFile}>{f}</span>)}
                    </div>
                  )}
                  {dropPreview.analysis.imageFiles.length > 0 && (
                    <div className={styles.previewGroup}>
                      <Badge color="green">이미지 ({dropPreview.analysis.imageFiles.length})</Badge>
                      {dropPreview.analysis.imageFiles.length <= 10
                        ? dropPreview.analysis.imageFiles.map((f) => <span key={f} className={styles.previewFile}>{f}</span>)
                        : <span className={styles.previewFile}>{dropPreview.analysis.imageFiles.slice(0, 5).join(', ')} 외 {dropPreview.analysis.imageFiles.length - 5}개</span>
                      }
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className={styles.previewActions}>
              <Button variant="outline" onClick={() => setDropPreview(null)}>취소</Button>
              <Button onClick={handleConfirmUpload}>업로드</Button>
            </div>
          </div>
        )}
        {dropPreview && uploading && (
          <div className={styles.uploadStatus}>
            <Spinner />
            <span>{uploadProgress}</span>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="로딩 디자인 삭제"
        message={`"${deleteTarget?.name}" 로딩 디자인을 삭제하시겠습니까?`}
        confirmLabel="삭제"
        variant="danger"
        loading={deleting}
      />
    </div>
  )
}
